import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ViewMasterService } from 'src/app/services/view-master/view-master.service';
import { ViewMasterModalService } from 'src/app/services/view-master-modal/view-master-modal.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-md-view-master',
  templateUrl: './md-view-master.component.html',
  styleUrls: ['./md-view-master.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MdViewMasterComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Math reference for template
  Math = Math;

  // Data properties
  viewMasters: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    date_from: '',
    date_to: ''
  };

  constructor(
    private _viewMasterService: ViewMasterService,
    private _viewMasterModalService: ViewMasterModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadViewMasters();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  // Helper method to display vehicle fields
  getVehicleFieldsDisplay(vehicleFields: any): string {
    if (!vehicleFields) return '-';

    if (Array.isArray(vehicleFields)) {
      return vehicleFields.length > 0 ? vehicleFields.join(', ') : '-';
    }

    return vehicleFields.toString();
  }

  // Helper method to display charges
  getChargesDisplay(charges: any): string {
    if (!charges) return '-';

    if (Array.isArray(charges)) {
      return charges.length > 0 ? charges.join(', ') : '-';
    }

    return charges.toString();
  }

  private initializeMenus(): void {
    setTimeout(() => {
      this.refreshMenus();
    }, 100);
  }

  private refreshMenus(): void {
    try {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    } catch (error) {
      console.warn('KTMenu initialization failed:', error);
    }
  }

  loadViewMasters(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    this.subscriptions.push(
      this._viewMasterService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.viewMasters = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.viewMasters = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading view masters:', error);
          this.viewMasters = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
    this.currentPage = 1;
  }

  onSearch(): void {
    this.loadViewMasters(1, this.searchTerm, this.filters);
  }

  onPageChange(page: number): void {
    this.loadViewMasters(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadViewMasters(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {
      date_from: '',
      date_to: ''
    };
    this.showFiltersForm = false;
    this.loadViewMasters();
  }

  openAddViewMasterModal(): void {
    this._viewMasterModalService.clearSelectedViewMaster();
    const modalElement = document.getElementById('kt_modal_view_master_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditViewMasterModal(viewMaster: any): void {
    this._viewMasterModalService.setSelectedViewMaster(viewMaster);
    const modalElement = document.getElementById('kt_modal_view_master_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteViewMaster(viewMaster: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${viewMaster.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(viewMaster);
      }
    });
  }

  private performDelete(viewMaster: any): void {
    const viewMasterId = viewMaster._id || viewMaster.id;
    this.subscriptions.push(
      this._viewMasterService.DeleteDataById(viewMasterId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'View master has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadViewMasters(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete view master.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error deleting view master:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while deleting the view master.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onViewMasterAdded(): void {
    console.log('View master added, refreshing list...');
    this.loadViewMasters(this.currentPage, this.searchTerm, this.filters);
  }

  onViewMasterUpdated(): void {
    console.log('View master updated, refreshing list...');
    this.loadViewMasters(this.currentPage, this.searchTerm, this.filters);
  }
}
